import { useState, FormEvent, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import DaumPostcodeSearch from '@/components/base/DaumPostcodeSearch';
import ValidationModal from './ValidationModal';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';
type AddressValue = { zonecode: string; roadAddress: string; jibunAddress: string; detailAddress: string };

function generateAdminCode(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const rand = Math.floor(1000 + Math.random() * 9000);
  return `KC-${y}${m}${d}-${rand}`;
}

function generateReceiptNumber(): string {
  const now = new Date();
  const yy = now.getFullYear().toString().slice(2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  const rand = String(Math.floor(Math.random() * 9000) + 1000);
  return `RC${yy}${mm}${dd}-${rand}`;
}

function getPhoneError(part1: string, part2: string, part3: string, label: string, required: boolean): string | null {
  const hasAny = part1 || part2 || part3;
  if (!hasAny) {
    return required ? `${label} 연락처` : null;
  }
  if (!part1 || !part2 || !part3) {
    return `${label} 연락처 (010-0000-0000 형식으로 모두 입력해 주세요)`;
  }
  if (part1 !== '010') {
    return `${label} 연락처 (맨 앞은 010으로 입력해 주세요)`;
  }
  if (part2.length < 3 || part2.length > 4) {
    return `${label} 연락처 (가운데 자리는 3~4자리)`;
  }
  if (part3.length !== 4) {
    return `${label} 연락처 (마지막 자리는 4자리)`;
  }
  return null;
}

const PRIVACY_TEXT = `코리아케어러스(이하 '회사'라 한다)는 개인정보 보호법 제30조에 따라 정보 주체의 개인정보를 보호하고 이와 관련한 고충을 신속하고 원활하게 처리할 수 있도록 하기 위하여 다음과 같이 개인정보 처리지침을 수립, 공개합니다.

제1조 (개인정보의 처리목적)
회사는 다음의 목적을 위하여 개인정보를 처리합니다.
1. 가족간병인 등록 및 보험 청구 서비스 제공
2. 보험금 청구를 위한 서류 접수 및 대행
3. 고객 상담 및 안내, 민원 처리
4. 간병 서비스 제공 및 보험금 청구 업무 수행을 위한 증빙 및 확인

제2조 (수집하는 개인정보 항목)
성명, 연락처, 주소, 성별, 생년월일(간병인), 보험사 정보 등
생년월일(6자리)은 보험 청구 서류 검토 및 본인 확인을 위해 수집하며, 목적 외 용도로는 사용되지 않습니다.

제3조 (개인정보의 처리 및 보유기간)
회사는 법령에 따른 개인정보 보유·이용 기간 또는 정보주체로부터 동의 받은 기간 내에서 개인정보를 처리·보유합니다.

제4조 (개인정보의 제3자 제공)
회사는 정보주체의 동의, 법률의 특별한 규정 등 개인정보 보호법에 해당하는 경우에만 개인정보를 제3자에게 제공합니다.`;

const THIRD_PARTY_TEXT = `다음과 같이 개인정보를 제3자에게 제공하고 있습니다.

1. 개인정보를 제공받는 자 : 보험회사, 병원, 청구 대행 협력사 등
2. 제공받는 자의 개인정보 이용목적 : 보험금 청구, 서류 검토 및 발급
3. 제공하는 개인정보 항목 : 상기 수집 정보 전부 또는 일부
4. 목적 달성 시까지 또는 법령에 따른 보관 기간`;

export default function RegisterForm() {
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [adminCode, setAdminCode] = useState('');
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [thirdPartyChecked, setThirdPartyChecked] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [validationOpen, setValidationOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const formRef = useRef<HTMLFormElement>(null);

  // Phone fields state
  const [carePhone1, setCarePhone1] = useState('');
  const [carePhone2, setCarePhone2] = useState('');
  const [carePhone3, setCarePhone3] = useState('');
  const [insPhone1, setInsPhone1] = useState('');
  const [insPhone2, setInsPhone2] = useState('');
  const [insPhone3, setInsPhone3] = useState('');
  const [guardianPhone1, setGuardianPhone1] = useState('');
  const [guardianPhone2, setGuardianPhone2] = useState('');
  const [guardianPhone3, setGuardianPhone3] = useState('');
  const [agentPhone1, setAgentPhone1] = useState('');
  const [agentPhone2, setAgentPhone2] = useState('');
  const [agentPhone3, setAgentPhone3] = useState('');

  // SSN fields state
  const [ssnFront, setSsnFront] = useState('');

  // Address state
  const [caregiverAddressValue, setCaregiverAddressValue] = useState<AddressValue>({ zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' });
  const [insuredAddressValue, setInsuredAddressValue] = useState<AddressValue>({ zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    const form = e.currentTarget;
    const fd = new FormData(form);

    const caregiverName = String(fd.get('caregiverName') || '').trim();
    const caregiverGender = String(fd.get('caregiverGender') || '');
    const insuredName = String(fd.get('insuredName') || '').trim();
    const insuredBirth = String(fd.get('insuredBirth') || '').trim();
    const insuredGender = String(fd.get('insuredGender') || '');

    const errors: string[] = [];

    // 동의 항목
    if (!privacyChecked) errors.push('개인정보 수집 및 이용 동의');
    if (!thirdPartyChecked) errors.push('개인정보 제3자 제공 동의');

    // 간병인 정보
    if (!caregiverName) errors.push('간병인 성명');
    const carePhoneErr = getPhoneError(carePhone1, carePhone2, carePhone3, '간병인', true);
    if (carePhoneErr) errors.push(carePhoneErr);
    if (ssnFront.length !== 6) errors.push('간병인 생년월일 6자리');
    if (!caregiverGender) errors.push('간병인 성별');
    if (!caregiverAddressValue.roadAddress && !caregiverAddressValue.jibunAddress) errors.push('간병인 주소');

    // 환자 정보
    if (!insuredName) errors.push('환자 성명');
    const insPhoneErr = getPhoneError(insPhone1, insPhone2, insPhone3, '환자', true);
    if (insPhoneErr) errors.push(insPhoneErr);
    if (insuredBirth.length !== 6) errors.push('환자 생년월일 6자리');
    if (!insuredGender) errors.push('환자 성별');
    if (!insuredAddressValue.roadAddress && !insuredAddressValue.jibunAddress) errors.push('환자 주소');

    // 선택 항목 (일부 입력 시에만 형식 검증)
    const guardianPhoneErr = getPhoneError(guardianPhone1, guardianPhone2, guardianPhone3, '보호자', false);
    if (guardianPhoneErr) errors.push(guardianPhoneErr);
    const agentPhoneErr = getPhoneError(agentPhone1, agentPhone2, agentPhone3, '설계사', false);
    if (agentPhoneErr) errors.push(agentPhoneErr);

    if (errors.length > 0) {
      setValidationErrors(errors);
      setValidationOpen(true);
      return;
    }

    const carePhone = [carePhone1, carePhone2, carePhone3].filter(Boolean).join('-');
    const insPhone = [insPhone1, insPhone2, insPhone3].filter(Boolean).join('-');
    const agentPhone = [agentPhone1, agentPhone2, agentPhone3].filter(Boolean).join('-');
    const guardianPhone = [guardianPhone1, guardianPhone2, guardianPhone3].filter(Boolean).join('-');

    const code = generateAdminCode();
    const now = new Date();
    const receiptNumber = generateReceiptNumber();

    const supabasePayload = {
      receipt_time: now.toISOString(),
      receipt_number: receiptNumber,
      caregiver_name: String(fd.get('caregiverName') || ''),
      caregiver_phone: carePhone,
      caregiver_birth: ssnFront,
      caregiver_ssn: ssnFront,
      caregiver_gender: String(fd.get('caregiverGender') || ''),
      caregiver_address: `(${caregiverAddressValue.zonecode}) ${caregiverAddressValue.roadAddress}${caregiverAddressValue.detailAddress ? ', ' + caregiverAddressValue.detailAddress : ''}`,
      patient_name: String(fd.get('insuredName') || ''),
      patient_phone: insPhone || '',
      patient_birth: String(fd.get('insuredBirth') || ''),
      patient_gender: String(fd.get('insuredGender') || ''),
      patient_address: `(${insuredAddressValue.zonecode}) ${insuredAddressValue.roadAddress}${insuredAddressValue.detailAddress ? ', ' + insuredAddressValue.detailAddress : ''}`,
      insurance_company: String(fd.get('insuranceCompany') || '') || null,
      agent_phone: agentPhone || null,
      guardian_name: String(fd.get('guardianName') || '') || null,
      guardian_phone: guardianPhone || null,
      status: '접수',
      admin_code: code,
    };

    // Readdy form data
    const data = new URLSearchParams();
    fd.forEach((val, key) => {
      if (typeof val === 'string') data.append(key, val);
    });
    data.set('caregiverPhone', carePhone);
    data.set('insuredPhone', insPhone);
    data.set('caregiverAddress', caregiverAddressValue.roadAddress);
    data.set('caregiverAddressDetail', caregiverAddressValue.detailAddress);
    data.set('insuredAddress', insuredAddressValue.roadAddress);
    data.set('insuredAddressDetail', insuredAddressValue.detailAddress);
    if (agentPhone) data.set('agentPhone', agentPhone);
    const guardianName = fd.get('guardianName');
    if (guardianName) data.set('guardianName', String(guardianName));
    if (guardianPhone) data.set('guardianPhone', guardianPhone);
    data.append('adminCode', code);
    data.append('privacyAgree', privacyChecked ? '동의' : '미동의');
    data.append('thirdPartyAgree', thirdPartyChecked ? '동의' : '미동의');

    setStatus('loading');

    try {
      // 1. Supabase 저장 (필수)
      const { error: dbError } = await supabase.from('care_requests').insert([supabasePayload]);
      if (dbError) {
        setStatus('error');
        setErrorMsg(`저장 중 오류가 발생했습니다: ${dbError.message}`);
        return;
      }

      // 2. 담당자 SMS 알림 발송 (비동기, 실패해도 무시하되 콘솔에 기록)
      supabase.functions.invoke('send-notification', {
        body: {
          type: 'new_registration',
          receipt_number: supabasePayload.receipt_number,
          receipt_time: supabasePayload.receipt_time,
          caregiver_name: supabasePayload.caregiver_name,
          caregiver_phone: supabasePayload.caregiver_phone,
          patient_name: supabasePayload.patient_name || null,
          patient_phone: supabasePayload.patient_phone || null,
          insurance_company: supabasePayload.insurance_company || null,
          admin_code: code,
        },
      }).then(({ error, data }) => {
        if (error) {
          console.error('[SMS] 문자 알림 호출 실패:', error);
        } else if (data && !data.success) {
          console.error('[SMS] 문자 발송 실패:', data.error);
        } else if (data?.sms && data.sms.success === false) {
          console.error('[SMS] 문자 발송 실패:', data.sms.error);
        }
      }).catch((err) => console.error('[SMS] 문자 알림 예외:', err));

      // 3. 지역 간병인에게 푸쉬 알림 발송 (비동기, 실패해도 무시)
      if (supabasePayload.patient_address) {
        supabase.functions.invoke('notify-caregivers', {
          body: {
            patient_address: supabasePayload.patient_address,
            patient_name: supabasePayload.patient_name || '익명',
          },
        }).catch(() => { /* 무시 */ });
      }

      // 4. Readdy form도 보냄 (비동기, 실패해도 무시)
      fetch('https://readdy.ai/api/form/d7cc9n1noju8nk5a0pcg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      }).catch(() => { /* 무시 */ });

      setAdminCode(code);
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handleReset = () => {
    setStatus('idle');
    setAdminCode('');
    setPrivacyChecked(false);
    setThirdPartyChecked(false);
    setErrorMsg('');
    setValidationOpen(false);
    setValidationErrors([]);
    setCarePhone1(''); setCarePhone2(''); setCarePhone3('');
    setInsPhone1(''); setInsPhone2(''); setInsPhone3('');
    setAgentPhone1(''); setAgentPhone2(''); setAgentPhone3('');
    setGuardianPhone1(''); setGuardianPhone2(''); setGuardianPhone3('');
    setSsnFront('');
    setCaregiverAddressValue({ zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' });
    setInsuredAddressValue({ zonecode: '', roadAddress: '', jibunAddress: '', detailAddress: '' });
    formRef.current?.reset();
  };

  if (status === 'success') {
    return (
      <section id="register-form" className="w-full py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div
            className="w-20 h-20 flex items-center justify-center rounded-full mx-auto mb-6"
            style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}
          >
            <i className="ri-checkbox-circle-line text-4xl" style={{ color: '#2E7D6B' }}></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">등록 신청이 완료되었습니다!</h2>
          <p className="text-sm text-gray-500 leading-relaxed mb-4">
            담당자가 확인 후 빠른 시간 내에 연락드립니다.<br />
            등록 후 간병인은 간병 시 <strong className="text-gray-700">일지를 반드시 작성</strong>해 주셔야 합니다.
          </p>
          <p className="text-xs text-gray-400 leading-relaxed mb-6">
            손해보험사 가입자는 간병일지 양식을 활용해 주시고,<br />
            생명보험사 가입자는 당사 고객센터로 연락 부탁드립니다.
          </p>

          <div
            className="inline-block px-8 py-5 rounded-2xl mb-8"
            style={{ backgroundColor: '#f0f9f6', border: '1.5px dashed #2E7D6B' }}
          >
            <p className="text-xs font-semibold text-gray-500 mb-1 tracking-widest uppercase">관리번호 (Admin Code)</p>
            <p className="text-2xl font-black tracking-widest" style={{ color: '#2E7D6B' }}>{adminCode}</p>
            <p className="text-xs text-gray-400 mt-2">이 번호를 메모해 두시면 문의 시 빠른 처리가 가능합니다.</p>
          </div>

          {/* 간병일지 다운로드 버튼 */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center mb-8">
            <a
              href="/carejournal-form.jpg"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#2E7D6B' }}
            >
              <i className="ri-image-line"></i>간병일지 양식 (JPG)
            </a>
            <a
              href="/carejournal-form.pdf"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#f0f9f6', color: '#2E7D6B', border: '1px solid #2E7D6B' }}
            >
              <i className="ri-file-pdf-line"></i>간병일지 양식 (PDF)
            </a>
          </div>

          <div
            className="rounded-2xl p-5 mb-6 text-left"
            style={{ backgroundColor: '#f4faf8', border: '1px solid rgba(46,125,107,0.2)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-phone-line text-base" style={{ color: '#2E7D6B' }}></i>
              <span className="text-sm font-bold text-gray-900">코리아케어러스 고객센터</span>
            </div>
            <a href="tel:15668654" className="text-2xl font-black block cursor-pointer mb-1" style={{ color: '#2E7D6B' }}>
              1566-8654
            </a>
            <p className="text-xs text-gray-400">오전 9시 ~ 오후 11시 · 연중무휴</p>
          </div>

          <button
            onClick={handleReset}
            className="px-7 py-3.5 rounded-xl text-sm font-bold text-white cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
            style={{ backgroundColor: '#2E7D6B' }}
          >
            다시 등록하기
          </button>
        </div>
      </section>
    );
  }

  return (
    <section id="register-form" className="w-full py-16 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <form
          id="family-register-form"
          data-readdy-form
          ref={formRef}
          onSubmit={handleSubmit}
          noValidate
          className="space-y-6"
        >
          {/* ───── 간병인 정보 ───── */}
          <FormCard>
            <SectionTitle icon="ri-user-line" title="간병인 정보" />
            <div className="space-y-4">
              <Field label="간병인 성명" required>
                <input
                  type="text"
                  name="caregiverName"
                  required
                  placeholder="이름을 입력하세요"
                  className={inputCls}
                />
              </Field>

              <Field label="간병인 연락처" required>
                <PhoneInput
                  v1={carePhone1} v2={carePhone2} v3={carePhone3}
                  onChange1={setCarePhone1} onChange2={setCarePhone2} onChange3={setCarePhone3}
                  required
                />
              </Field>

              <Field label="간병인 생년월일 6자리 (주민번호X)" required>
                <div className="space-y-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={ssnFront}
                    onChange={(e) => setSsnFront(e.target.value.replace(/\D/g, ''))}
                    placeholder="예: 850101"
                    autoComplete="off"
                    className={`${inputCls} text-center tracking-widest`}
                    required
                  />
                </div>
              </Field>

              <Field label="성별" required>
                <div className="flex gap-4">
                  {['남', '여'].map((g) => (
                    <label key={g} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="caregiverGender"
                        value={g}
                        required
                        className="w-4 h-4 cursor-pointer accent-[#2E7D6B]"
                      />
                      <span className="text-sm text-gray-700">{g}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="간병인 주소" required>
                <DaumPostcodeSearch
                  value={caregiverAddressValue}
                  onChange={setCaregiverAddressValue}
                  placeholder="주소를 검색해주세요"
                />
              </Field>
            </div>
          </FormCard>

          {/* ───── 환자 정보 ───── */}
          <FormCard>
            <SectionTitle icon="ri-shield-user-line" title="환자 정보" required />
            <div className="space-y-4">
              <Field label="환자 성명" required>
                <input
                  type="text"
                  name="insuredName"
                  required
                  placeholder="이름을 입력하세요"
                  className={inputCls}
                />
              </Field>

              <Field label="환자 연락처" required>
                <PhoneInput
                  v1={insPhone1} v2={insPhone2} v3={insPhone3}
                  onChange1={setInsPhone1} onChange2={setInsPhone2} onChange3={setInsPhone3}
                  required
                />
              </Field>

              <Field label="환자 생년월일 6자리 (주민번호X)" required>
                <input
                  type="text"
                  name="insuredBirth"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  placeholder="예: 551030"
                  className={inputCls}
                />
              </Field>

              <Field label="성별" required>
                <div className="flex gap-4">
                  {['남', '여'].map((g) => (
                    <label key={g} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="insuredGender"
                        value={g}
                        required
                        className="w-4 h-4 cursor-pointer accent-[#2E7D6B]"
                      />
                      <span className="text-sm text-gray-700">{g}</span>
                    </label>
                  ))}
                </div>
              </Field>

              <Field label="환자 주소" required>
                <DaumPostcodeSearch
                  value={insuredAddressValue}
                  onChange={setInsuredAddressValue}
                  placeholder="주소를 검색해주세요"
                />
              </Field>
            </div>
          </FormCard>

          {/* ───── 보호자 정보 ───── */}
          <FormCard>
            <SectionTitle icon="ri-user-heart-line" title="보호자 정보" sub="선택사항" />
            <div className="space-y-4">
              <Field label="보호자 성명">
                <input
                  type="text"
                  name="guardianName"
                  placeholder="이름을 입력하세요"
                  className={inputCls}
                />
              </Field>

              <Field label="보호자 연락처">
                <PhoneInput
                  v1={guardianPhone1} v2={guardianPhone2} v3={guardianPhone3}
                  onChange1={setGuardianPhone1} onChange2={setGuardianPhone2} onChange3={setGuardianPhone3}
                />
              </Field>
            </div>
          </FormCard>

          {/* ───── 가입 보험사 ───── */}
          <FormCard>
            <SectionTitle icon="ri-building-2-line" title="가입 보험사" />
            <Field label="가입 보험사">
              <input
                type="text"
                name="insuranceCompany"
                placeholder="예: 삼성생명, DB손해보험 등"
                className={inputCls}
              />
            </Field>
          </FormCard>

          {/* ───── 설계사 연락처 ───── */}
          <FormCard>
            <SectionTitle icon="ri-contacts-line" title="설계사 연락처" sub="설계사가 등록 시에만 기재" />
            <Field label="설계사 연락처">
              <PhoneInput
                v1={agentPhone1} v2={agentPhone2} v3={agentPhone3}
                onChange1={setAgentPhone1} onChange2={setAgentPhone2} onChange3={setAgentPhone3}
              />
            </Field>
          </FormCard>

          {/* ───── 개인정보 수집 및 이용 동의 ───── */}
          <FormCard>
            <SectionTitle icon="ri-lock-line" title="개인정보 수집 및 이용 동의" required />
            <div
              className="w-full h-44 overflow-y-auto rounded-xl border border-gray-200 p-4 text-xs text-gray-500 leading-relaxed mb-4 bg-gray-50 whitespace-pre-line"
            >
              {PRIVACY_TEXT}
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyChecked}
                onChange={(e) => setPrivacyChecked(e.target.checked)}
                className="w-4 h-4 cursor-pointer accent-[#2E7D6B] flex-shrink-0"
              />
              <span className="text-sm text-gray-700 font-medium">개인정보 수집 및 이용에 동의합니다.</span>
            </label>
          </FormCard>

          {/* ───── 개인정보 제3자 제공 동의 ───── */}
          <FormCard>
            <SectionTitle icon="ri-share-line" title="개인정보 제3자 제공 동의" required />
            <div
              className="w-full h-36 overflow-y-auto rounded-xl border border-gray-200 p-4 text-xs text-gray-500 leading-relaxed mb-4 bg-gray-50 whitespace-pre-line"
            >
              {THIRD_PARTY_TEXT}
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={thirdPartyChecked}
                onChange={(e) => setThirdPartyChecked(e.target.checked)}
                className="w-4 h-4 cursor-pointer accent-[#2E7D6B] flex-shrink-0"
              />
              <span className="text-sm text-gray-700 font-medium">개인정보 제3자 제공에 대해 동의합니다.</span>
            </label>
          </FormCard>

          {/* 에러 메시지 */}
          {errorMsg && (
            <p className="text-sm text-center text-red-500 font-medium">{errorMsg}</p>
          )}

          {/* 제출 버튼 */}
          <button
            type="submit"
            disabled={status === 'loading'}
            className="w-full py-4 rounded-xl text-base font-bold text-white whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ backgroundColor: '#2E7D6B' }}
          >
            {status === 'loading' ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>제출 중...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-user-add-line"></i>등록
              </span>
            )}
          </button>

          {status === 'error' && (
            <p className="text-center text-xs text-red-500">
              제출 중 오류가 발생했습니다. 다시 시도하거나{' '}
              <a href="tel:15668654" className="font-semibold underline">전화로 문의</a>해 주세요.
            </p>
          )}
        </form>
      </div>

      <ValidationModal
        open={validationOpen}
        errors={validationErrors}
        onClose={() => setValidationOpen(false)}
      />
    </section>
  );
}

/* ─── 공통 스타일 ─── */
const inputCls =
  'w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none transition-all focus:border-[#2E7D6B] bg-white';

/* ─── 폼 카드 ─── */
function FormCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="rounded-2xl border border-gray-100 p-6"
      style={{ backgroundColor: '#fafffe' }}
    >
      {children}
    </div>
  );
}

/* ─── 섹션 타이틀 ─── */
function SectionTitle({
  icon,
  title,
  sub,
  required,
}: {
  icon: string;
  title: string;
  sub?: string;
  required?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 mb-5 pb-4 border-b border-gray-100">
      <div
        className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
        style={{ backgroundColor: 'rgba(46,125,107,0.1)' }}
      >
        <i className={`${icon} text-sm`} style={{ color: '#2E7D6B' }}></i>
      </div>
      <h3 className="text-sm font-bold text-gray-900">
        {title}
        {required && <span className="text-red-400 ml-1">*</span>}
      </h3>
      {sub && <span className="text-xs text-gray-400">({sub})</span>}
    </div>
  );
}

/* ─── 필드 래퍼 ─── */
function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
        {label}{required && <span className="text-red-400 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

/* ─── 전화번호 입력 ─── */
function PhoneInput({
  v1, v2, v3,
  onChange1, onChange2, onChange3,
  required = false,
}: {
  v1: string; v2: string; v3: string;
  onChange1: (v: string) => void;
  onChange2: (v: string) => void;
  onChange3: (v: string) => void;
  required?: boolean;
}) {
  const ref2 = useRef<HTMLInputElement>(null);
  const ref3 = useRef<HTMLInputElement>(null);

  const handlePart1Change = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    onChange1(cleaned);
    if (cleaned.length >= 3) ref2.current?.focus();
  };

  const handlePart2Change = (value: string) => {
    const cleaned = value.replace(/\D/g, '');
    onChange2(cleaned);
    if (cleaned.length >= 4) ref3.current?.focus();
  };

  const handlePart2KeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !v2) {
      const input = e.currentTarget;
      const prev = input.previousElementSibling?.previousElementSibling as HTMLInputElement | null;
      if (prev) setTimeout(() => { prev.focus(); prev.setSelectionRange(prev.value.length, prev.value.length); }, 0);
    }
  };

  const handlePart3KeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !v3) {
      setTimeout(() => ref2.current?.focus(), 0);
    }
  };

  const baseStyle =
    'w-full px-3 py-3 rounded-xl border border-gray-200 text-sm outline-none transition-all focus:border-[#2E7D6B] bg-white text-center';
  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        inputMode="numeric"
        maxLength={3}
        value={v1}
        onChange={(e) => handlePart1Change(e.target.value)}
        placeholder="010"
        autoComplete="off"
        required={required}
        className={baseStyle}
      />
      <span className="text-gray-400 font-bold flex-shrink-0">-</span>
      <input
        type="text"
        inputMode="numeric"
        maxLength={4}
        value={v2}
        onChange={(e) => handlePart2Change(e.target.value)}
        onKeyDown={handlePart2KeyDown}
        ref={ref2}
        placeholder="0000"
        autoComplete="off"
        required={required}
        className={baseStyle}
      />
      <span className="text-gray-400 font-bold flex-shrink-0">-</span>
      <input
        type="text"
        inputMode="numeric"
        maxLength={4}
        value={v3}
        onChange={(e) => onChange3(e.target.value.replace(/\D/g, ''))}
        onKeyDown={handlePart3KeyDown}
        ref={ref3}
        placeholder="0000"
        autoComplete="off"
        required={required}
        className={baseStyle}
      />
    </div>
  );
}